import { Address } from '../address.js';
import { Helper } from './commonAnswersTypes.js';

export interface Demographics {
  gender: string;
  race: string[];
  ethnicity: string;
}
export type HouseholdMember = Demographics & {
  name: string;
  dateOfBirth: Date | string;
};

interface Landlord {
  contactName: string;
  companyName?: string;
  phone: string;
  email: string;
}

export interface HouseholdIncome {
  type?: string;
  frequency: number;
  amount: number;
  annualized: number;
}

export interface HouseholdMonthlyIncome {
  income: number;
  attestation: boolean;
}

export interface Signature {
  attestation: boolean;
  signature: string;
  date: string;
}

export interface Totals {
  pastTotal: number;
  futureTotal: number;
}

export enum AMIBracket {
  '0_30' = '0_30',
  '30_50' = '30_50',
  '50_80' = '50_80',
  '80+' = '80+',
}

export enum YesNoUnsure {
  Yes = 'yes',
  No = 'no',
  Unsure = 'unsure',
}
export interface BaseAnswers {
  demographics: Demographics;
  dateOfBirth: Date | string;
  ssn?: string;
  householdMembers?: HouseholdMember[];
  householdSize: number;
  amiBracket?: AMIBracket;
  householdIncome?: {
    monthlyAmount?: number;
    income: HouseholdIncome;
    attestation: boolean;
  };
  financialHardship: {
    statement: {
      qualifiedBenefits: 'yes' | 'no';
    };
    documents?: string[];
    attestation?: {
      explanation: string;
      signature: Signature;
    };
    experience?: {
      expenses: string[];
      attestation: boolean;
    };
    income?: {
      incomeReductions: string[];
      attestation: boolean;
    };
  };
  housingInstability: {
    receivedDocuments: ('rentPastDue' | 'utilityPastDue' | 'evictionNotice' | 'none')[];
    statement: {
      riskOfHomelessness: YesNoUnsure;
    };
    unsafeConditions?: {
      conditions: string[];
      attestation: boolean;
    };
    documents?: string[];
    attestation?: {
      explanation: string;
      signature: Signature;
    };
  };
  publicBenefits: {
    skipped?: boolean;
    qualifiedBenefits: string[];
  };
  submission: {
    assistance: {
      receivedAssistance: 'yes' | 'no';
      helper?: Helper;
    };
    signature: Signature;
  };
}

interface SecurityDepositAssistanceRequest {
  securityDepositAmount: number;
  monthlyRentAmount: number;
}

export type SecurityDepositAssistanceAnswers = BaseAnswers & {
  currentAddress: Omit<Address, 'id'>;
  futureAddress: Address;
  householdMembers: (HouseholdMember & { income: boolean; onLease: boolean })[];
  requests: {
    securityDepositAssistance: SecurityDepositAssistanceRequest;
  };
};

export interface RentalAssistanceRequest {
  pastDueAmount: number;
  monthlyRentAmount: number;
}

export interface Utility {
  type: string;
  provider: string;
  amount: number;
  accountNumber?: string;
}

export interface UtilityAssistanceRequest {
  pastDue: Utility[];
  current: Utility[];
  skipped?: boolean;
}

export interface OtherExpense {
  provider: string;
  amount: number;
}
export interface OtherExpensesRequest {
  expenses: OtherExpense[];
  skipped?: boolean;
}

export interface AdminOnly {
  verifiedRent?: number;
  verifiedArrearage?: number;
  hasIncomeDocs: string;
  hasBenefitsDocs?: string;
}

export type RentalAssistanceAnswers = BaseAnswers & {
  address: {
    // TODO: I think we need to figure some things out here; address in EN-1011
    addressId: string;
    address: { isUnhoused: boolean };
    landlordMailingAddressId: string;
    mailingAddressId: string;
    landlord: Landlord;
    documents?: string[];
    attestation?: {
      moveInDate: string;
      leaseExpirationDate: string;
      monthlyRentAmount: number;
      areUtilsIncluded: boolean;
      utilsInRentDesc?: string;
      docRentAmountDesc: string;
      hasOtherRA: boolean;
      otherRaAmount?: number;
      signature: Signature;
    };
  };
  householdMembers: HouseholdMember[];
  requests: {
    rentalAssistance?: RentalAssistanceRequest;
    utilityAssistance?: UtilityAssistanceRequest;
    otherExpenses?: OtherExpensesRequest;
  };
  documents: {
    skipped?: boolean;
    residency: string[];
    income: string[];
    rentAmount: string[];
    utilityBills: string[];
    internetBills: string[];
  };
  adminOnly?: AdminOnly;
};
